/**
 * Keycloak OIDC Authorization Code + PKCE (S256).
 * Mirrors services/admin-console/src/auth.ts for the agent-console client.
 */

import { getAuthConfig, isOidcConfigured, oidcBase } from './config'

const KEY_TOKEN = 'w7_access_token'
const KEY_ID_TOKEN = 'w7_id_token'
const KEY_REFRESH = 'w7_refresh_token'
const KEY_VERIFIER = 'w7_pkce_verifier'
const KEY_STATE = 'w7_pkce_state'
const KEY_NEXT = 'w7_auth_next'
const KEY_DEMO = 'w7_demo_session'

export interface Claims {
  sub: string
  agentId?: string
  roles: string[]
  preferredUsername?: string
  email?: string
  exp?: number
  raw: Record<string, unknown>
}

function b64url(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function rand(n: number): string {
  const a = new Uint8Array(n)
  crypto.getRandomValues(a)
  return b64url(a)
}

async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return b64url(new Uint8Array(digest))
}

function redirectUri(): string {
  return `${location.origin}/callback`
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(KEY_TOKEN)
}

export function isDemoSession(): boolean {
  return sessionStorage.getItem(KEY_DEMO) === '1'
}

export function startDemoSession(): void {
  clearOidcTokens()
  sessionStorage.setItem(KEY_DEMO, '1')
}

export function clearDemoSession(): void {
  sessionStorage.removeItem(KEY_DEMO)
}

function clearOidcTokens(): void {
  sessionStorage.removeItem(KEY_TOKEN)
  sessionStorage.removeItem(KEY_ID_TOKEN)
  sessionStorage.removeItem(KEY_REFRESH)
  sessionStorage.removeItem(KEY_VERIFIER)
  sessionStorage.removeItem(KEY_STATE)
}

export function clearAllAuth(): void {
  clearOidcTokens()
  clearDemoSession()
  sessionStorage.removeItem(KEY_NEXT)
  callbackInFlight = null
}

export function decodeClaims(token: string | null): Claims | null {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join(''),
      ),
    ) as Record<string, unknown>
    const realmAccess = (json.realm_access ?? {}) as { roles?: string[] }
    const resource = (json.resource_access ?? {}) as Record<string, { roles?: string[] }>
    const clientRoles = resource['agent-console']?.roles ?? []
    const roles = [...new Set([...(realmAccess.roles ?? []), ...clientRoles])]
    return {
      sub: String(json.sub ?? ''),
      agentId: json.agentId ? String(json.agentId) : undefined,
      roles,
      preferredUsername: json.preferred_username ? String(json.preferred_username) : undefined,
      email: json.email ? String(json.email) : undefined,
      exp: typeof json.exp === 'number' ? json.exp : undefined,
      raw: json,
    }
  } catch {
    return null
  }
}

export function claims(): Claims | null {
  return decodeClaims(getAccessToken())
}

export function isAccessTokenExpired(c: Claims | null = claims()): boolean {
  if (!c?.exp) return false
  // 30s skew
  return Date.now() / 1000 >= c.exp - 30
}

/** Console access: agentId or platform role (journey agent-console-login). */
export function hasConsoleAccess(c: Claims | null): boolean {
  if (!c) return false
  if (c.agentId) return true
  return c.roles.some((r) => r === 'agent' || r === 'ops' || r === 'risk-desk')
}

export function rememberNext(path: string): void {
  if (!path || path === '/login' || path === '/callback') return
  sessionStorage.setItem(KEY_NEXT, path)
}

export function consumeNext(fallback = '/'): string {
  const next = sessionStorage.getItem(KEY_NEXT) ?? fallback
  sessionStorage.removeItem(KEY_NEXT)
  return next.startsWith('/') ? next : fallback
}

export async function beginOidcLogin(loginHint?: string): Promise<void> {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) {
    throw new Error('Sign-in is not configured. Set VITE_KEYCLOAK_URL to enable Keycloak.')
  }
  // New login attempt — allow a fresh /callback exchange (SPA keeps module state).
  callbackInFlight = null
  clearDemoSession()
  const verifier = rand(48)
  const state = rand(16)
  sessionStorage.setItem(KEY_VERIFIER, verifier)
  sessionStorage.setItem(KEY_STATE, state)
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: 'code',
    scope: 'openid profile email',
    redirect_uri: redirectUri(),
    state,
    code_challenge: await challenge(verifier),
    code_challenge_method: 'S256',
  })
  if (loginHint) params.set('login_hint', loginHint)
  location.assign(`${oidcBase(cfg)}/auth?${params}`)
}

/** Keycloak application-initiated actions for MFA enroll (branded console → IdP ceremony). */
export type KcMfaAction =
  | 'CONFIGURE_TOTP'
  | 'webauthn-register'
  | 'CONFIGURE_RECOVERY_AUTHN_CODES'

/**
 * Start a Keycloak AIA while keeping W7 as the return surface.
 * SSO session usually skips password and opens only the enroll step.
 */
export async function beginKcAction(action: KcMfaAction, returnPath: string): Promise<void> {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) {
    throw new Error('Sign-in is not configured. Set VITE_KEYCLOAK_URL to enable Keycloak.')
  }
  if (isDemoSession()) {
    throw new Error('Demo session cannot enroll MFA — sign in with Keycloak first.')
  }
  callbackInFlight = null
  rememberNext(returnPath.startsWith('/') ? returnPath : '/settings/security')
  const verifier = rand(48)
  const state = rand(16)
  sessionStorage.setItem(KEY_VERIFIER, verifier)
  sessionStorage.setItem(KEY_STATE, state)
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: 'code',
    scope: 'openid profile email',
    redirect_uri: redirectUri(),
    state,
    code_challenge: await challenge(verifier),
    code_challenge_method: 'S256',
    kc_action: action,
  })
  location.assign(`${oidcBase(cfg)}/auth?${params}`)
}

export type CallbackResult =
  | { ok: true }
  | { ok: false; reason: 'missing-code' | 'state' | 'token' | 'network' | 'not-configured' }

/**
 * React Strict Mode mounts /callback twice in dev. The auth code is single-use,
 * so a second token exchange fails. Share one in-flight promise per page load.
 */
let callbackInFlight: Promise<CallbackResult> | null = null

export function handleOidcCallback(): Promise<CallbackResult> {
  if (!callbackInFlight) callbackInFlight = runOidcCallback()
  return callbackInFlight
}

/** Test helper — clears the single-flight latch between cases. */
export function resetOidcCallbackLatch(): void {
  callbackInFlight = null
}

async function runOidcCallback(): Promise<CallbackResult> {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) return { ok: false, reason: 'not-configured' }

  // Remount after a successful first exchange (tokens already stored).
  if (getAccessToken() && !isAccessTokenExpired()) return { ok: true }

  const url = new URL(location.href)
  const err = url.searchParams.get('error')
  if (err) return { ok: false, reason: 'token' }

  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')
  if (!code) return { ok: false, reason: 'missing-code' }
  if (state !== sessionStorage.getItem(KEY_STATE)) return { ok: false, reason: 'state' }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: sessionStorage.getItem(KEY_VERIFIER) ?? '',
  })

  let response: Response
  try {
    response = await fetch(`${oidcBase(cfg)}/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    })
  } catch {
    return { ok: false, reason: 'network' }
  }

  if (!response.ok) return { ok: false, reason: 'token' }

  const json = (await response.json()) as {
    access_token?: string
    id_token?: string
    refresh_token?: string
  }
  if (!json.access_token) return { ok: false, reason: 'token' }

  sessionStorage.setItem(KEY_TOKEN, json.access_token)
  if (json.id_token) sessionStorage.setItem(KEY_ID_TOKEN, json.id_token)
  if (json.refresh_token) sessionStorage.setItem(KEY_REFRESH, json.refresh_token)
  sessionStorage.removeItem(KEY_VERIFIER)
  sessionStorage.removeItem(KEY_STATE)
  clearDemoSession()
  return { ok: true }
}

export function logout(): void {
  const cfg = getAuthConfig()
  const idToken = sessionStorage.getItem(KEY_ID_TOKEN)
  const wasOidc = Boolean(getAccessToken())
  clearAllAuth()

  if (wasOidc && isOidcConfigured(cfg)) {
    const params = new URLSearchParams({
      client_id: cfg.clientId,
      post_logout_redirect_uri: `${location.origin}/login`,
    })
    if (idToken) params.set('id_token_hint', idToken)
    location.assign(`${oidcBase(cfg)}/logout?${params}`)
    return
  }
  location.assign('/login')
}

export type AuthPhase =
  | { phase: 'loading' }
  | { phase: 'anon' }
  | { phase: 'demo' }
  | { phase: 'no-access'; who: Claims }
  | { phase: 'ready'; who: Claims; mode: 'oidc' | 'demo' }
  | { phase: 'expired' }

export function resolveAuthPhase(): AuthPhase {
  if (isDemoSession()) {
    return { phase: 'ready', who: { sub: 'demo', roles: ['agent'], preferredUsername: 'demo', raw: {} }, mode: 'demo' }
  }
  const token = getAccessToken()
  if (!token) return { phase: 'anon' }
  const who = decodeClaims(token)
  if (!who) {
    clearOidcTokens()
    return { phase: 'anon' }
  }
  if (isAccessTokenExpired(who)) return { phase: 'expired' }
  if (!hasConsoleAccess(who)) return { phase: 'no-access', who }
  return { phase: 'ready', who, mode: 'oidc' }
}
